import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  FiCheck, FiChevronDown, FiArrowRight, FiShield, 
  FiZap, FiLayout, FiCode, FiSmartphone, FiShoppingCart, FiRefreshCw,
  FiTarget, FiClock, FiLayers, FiAlertCircle, FiMonitor
} from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { pageFaqs } from '../data/faqs';

const WebDesignDevelopment = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = "Web Design and Development | ManagedSite.net";
    
    const description = "ManagedSite.net provides web design and development support, including WordPress builds, redesigns, landing pages, WooCommerce projects, migrations and technical website improvements.";
    const title = "Web Design and Development | ManagedSite.net";
    
    const updateMeta = (selector, attr, value) => {
      const el = document.querySelector(selector);
      if (el) el.setAttribute(attr, value);
    };
    
    updateMeta('meta[name="description"]', 'content', description);
    updateMeta('meta[property="og:title"]', 'content', title);
    updateMeta('meta[property="og:description"]', 'content', description);
    updateMeta('meta[property="twitter:title"]', 'content', title);
    updateMeta('meta[property="twitter:description"]', 'content', description);
  }, []);

  const [openFaq, setOpenFaq] = useState(null);

  const whyAgencies = [
    {
      title: "Capacity Gaps",
      text: "Project volume rises faster than internal design and development capacity.",
      icon: FiLayers
    },
    {
      title: "Technical Complexity",
      text: "WooCommerce, integrations and migrations need experienced hands.",
      icon: FiCode
    },
    {
      title: "Tight Timelines",
      text: "Clients expect progress even when your team is already booked.",
      icon: FiClock
    },
    {
      title: "White-Label Delivery",
      text: "You need dependable help that can work behind your brand.",
      icon: FiShield
    }
  ];

  const buildImprove = [
    {
      title: "Website Design and Builds",
      description: "New WordPress website projects designed for clarity, conversion and long-term manageability.",
      examples: ["New website builds", "Brand-aligned page layouts", "Service and location pages", "Conversion-focused structure", "Launch support"],
      icon: FiLayout
    },
    {
      title: "Redesigns and Refreshes",
      description: "Improve existing websites that look dated, convert poorly or have become hard to maintain.",
      examples: ["Homepage redesigns", "Full site refreshes", "Content restructuring", "UX improvements", "Design system cleanup"],
      icon: FiRefreshCw
    },
    {
      title: "Landing Pages",
      description: "Focused pages for campaigns, offers, lead generation and service promotions.",
      examples: ["Campaign landing pages", "Lead-capture pages", "Offer pages", "Form integrations", "Tracking-ready structure"],
      icon: FiTarget
    },
    {
      title: "WooCommerce Projects",
      description: "Ecommerce builds and improvements for stores that need careful setup and ongoing stability.",
      examples: ["Store setup", "Product and checkout improvements", "Payment and shipping configuration support", "Store redesigns", "Ecommerce troubleshooting handoff"],
      icon: FiShoppingCart
    },
    {
      title: "Website Migrations",
      description: "Move websites between hosts or platforms with planning, testing and cutover support.",
      examples: ["Host-to-host migrations", "Platform migrations", "DNS cutover planning", "Post-migration validation", "Starting at $199"],
      icon: FiRefreshCw
    },
    {
      title: "Performance, Accessibility and Integrations",
      description: "Technical improvements that make websites faster, clearer and better connected.",
      examples: ["Performance optimization starting at $149", "Accessibility improvements", "Plugin and API integrations", "Form and CRM connections", "Technical cleanup"],
      icon: FiZap
    }
  ];

  const faqs = pageFaqs['/services/web-design-and-development'];

  return (
    <div className="w-full">
      {/* Hero Section */}
      <section className="relative bg-[#FAF8F3] overflow-hidden pt-24 pb-24 lg:pt-40 lg:pb-36">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="lg:grid lg:grid-cols-12 lg:gap-20 items-center">
            <div className="lg:col-span-7 text-center lg:text-left mb-20 lg:mb-0">
              <div className="text-sm font-bold tracking-[0.25em] text-[#D7B77A] uppercase mb-8">Web Design and Development</div>
              <h1 className="text-5xl sm:text-6xl lg:text-7xl font-extrabold text-[#17211E] tracking-tight leading-[1.1] mb-10">
                Design and Development Support When You Need Extra Hands
              </h1>
              <p className="text-xl sm:text-2xl text-[#17211E]/80 leading-relaxed mb-12 max-w-2xl mx-auto lg:mx-0 font-medium">
                Website projects, redesigns, landing pages, WooCommerce builds, migrations, and technical improvements — without adding full-time design or development staff.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                <Link to="/digital-review" className="home-button">
                  Discuss a Project
                </Link>
                <Link to="/agency-plans" className="home-button-secondary">
                  View Agency Plans
                </Link>
              </div>
            </div>
            <div className="lg:col-span-5 relative">
              <div className="relative rounded-[2rem] bg-[#EAF1EC] border border-[#3B7C6D]/20 shadow-2xl p-10 sm:p-12">
                <div className="absolute top-0 left-0 w-full h-2 bg-[#174C3C]"></div>
                <div className="space-y-6">
                  {[
                    { icon: FiLayout, label: "New Builds", status: "Available" },
                    { icon: FiRefreshCw, label: "Redesigns", status: "Supported" },
                    { icon: FiShoppingCart, label: "WooCommerce", status: "Capable" },
                    { icon: FiRefreshCw, label: "Migrations", status: "Planned" },
                    { icon: FiZap, label: "Performance", status: "Improved" },
                    { icon: FiShield, label: "Agency Delivery", status: "White-Label" }
                  ].map((item, i) => (
                    <div key={i} className="bg-white p-5 rounded-2xl flex items-center justify-between border border-gray-50">
                      <div className="flex items-center gap-4">
                        <div className="bg-[#EAF1EC] p-3 rounded-xl text-[#174C3C]">
                          <SafeIcon icon={item.icon} className="w-6 h-6" />
                        </div>
                        <span className="font-extrabold text-[#17211E]">{item.label}</span>
                      </div>
                      <span className="text-sm font-bold text-[#3B7C6D] bg-[#EAF1EC] px-3 py-1 rounded-full">{item.status}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Agencies Need This */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-20">
            <h2 className="text-4xl md:text-5xl font-bold text-[#17211E] mb-8 leading-tight">Keep Projects Moving Without Overloading Your Team</h2>
            <p className="text-xl text-[#17211E]/70 leading-relaxed font-medium">
              Agencies often need overflow help for builds, redesigns, migrations and technical development while still owning the client relationship.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {whyAgencies.map((item, i) => (
              <div key={i} className="bg-[#FAF8F3] p-10 rounded-3xl border border-[#EAF1EC] hover:shadow-md transition-all">
                <div className="text-[#174C3C] mb-8">
                  <SafeIcon icon={item.icon} className="w-10 h-10" />
                </div>
                <h3 className="text-2xl font-bold text-[#17211E] mb-4">{item.title}</h3>
                <p className="text-lg text-[#17211E]/75 leading-relaxed font-medium">{item.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What We Build and Improve */}
      <section className="py-24 bg-[#FAF8F3]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-20">
            <h2 className="text-4xl md:text-5xl font-bold text-[#17211E] mb-8 leading-tight">What Web Design and Development Covers</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
            {buildImprove.map((service, i) => (
              <div key={i} className="bg-white p-10 md:p-12 rounded-[2rem] border border-[#EAF1EC] shadow-sm flex flex-col h-full">
                <h3 className="text-2xl font-extrabold text-[#17211E] mb-6">{service.title}</h3>
                <p className="text-xl text-[#17211E]/75 mb-10 leading-relaxed font-medium">{service.description}</p>
                <div className="flex-grow">
                  <div className="text-sm font-bold text-[#3B7C6D] uppercase tracking-wider mb-6">Examples:</div>
                  <ul className="space-y-4">
                    {service.examples.map((item, j) => (
                      <li key={j} className="flex items-start text-[#17211E]/80 text-lg font-medium">
                        <SafeIcon icon={FiCheck} className="w-5 h-5 text-[#174C3C] mr-4 mt-1 flex-shrink-0" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How We Work with Agencies */}
      <section className="py-24 bg-[#EAF1EC]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 items-center">
            <div>
              <h2 className="text-4xl md:text-5xl font-bold text-[#17211E] mb-8 leading-tight">Overflow Support Behind Your Brand</h2>
              <p className="text-xl text-[#17211E]/75 leading-relaxed font-medium mb-12">
                ManagedSite.net can handle scoped design and development work while your agency remains the client-facing provider.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-12 gap-y-6 mb-16">
                {[
                  "White-label or behind-the-scenes delivery",
                  "Clear project scoping before work begins",
                  "Coordination with your project managers",
                  "Documentation for handoff",
                  "Optional ongoing management after launch",
                  "No direct solicitation of agency clients"
                ].map((item, i) => (
                  <div key={i} className="flex items-start text-lg font-bold text-[#17211E]">
                    <SafeIcon icon={FiCheck} className="w-6 h-6 text-[#174C3C] mr-4 flex-shrink-0 mt-1" />
                    {item}
                  </div>
                ))}
              </div>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/digital-review" className="home-button">
                  Discuss a Project
                </Link>
                <Link to="/services/website-management" className="home-button-secondary">
                  Explore Website Management
                </Link>
              </div>
            </div>
            <div className="hidden lg:block relative select-none opacity-50">
              <div className="bg-white/50 rounded-[3rem] p-16 border border-white/20 backdrop-blur-sm text-center">
                <div className="text-[#174C3C] text-6xl font-black mb-10">WL</div>
                <div className="h-1.5 w-24 bg-[#174C3C] mx-auto mb-10"></div>
                <div className="space-y-6">
                  <div className="h-4 bg-[#174C3C]/20 rounded-full w-full"></div>
                  <div className="h-4 bg-[#174C3C]/20 rounded-full w-5/6 mx-auto"></div>
                  <div className="h-4 bg-[#174C3C]/20 rounded-full w-4/6 mx-auto"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Project Approach */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl md:text-5xl font-bold text-[#17211E] mb-20 text-center leading-tight">How Design and Development Projects Work</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
            {[
              { title: "Scope", text: "We review goals, current assets, technical constraints and delivery expectations." },
              { title: "Plan", text: "We confirm the project approach, timeline considerations and responsibilities." },
              { title: "Build", text: "We design, develop, migrate or improve the website according to the agreed scope." },
              { title: "Hand Off", text: "We test key functionality, document the work and support launch or handback to your team." }
            ].map((step, i) => (
              <div key={i} className="relative">
                <div className="text-6xl font-black text-[#EAF1EC] mb-8">{i + 1}</div>
                <h3 className="text-2xl font-bold text-[#17211E] mb-5">{step.title}</h3>
                <p className="text-lg text-[#17211E]/75 leading-relaxed font-medium">{step.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Common Project Types */}
      <section className="py-24 bg-[#FAF8F3]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-[#17211E] mb-16 text-center">Common Project Types</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              "New WordPress websites", "Website redesigns", "Landing pages", "WooCommerce stores", 
              "Membership site builds", "Website migrations", "Performance optimization", 
              "Accessibility improvements", "CRM and form integrations", "Custom functionality", 
              "Staging-to-production launches", "Cleanup of inherited websites"
            ].map((type, i) => (
              <div key={i} className="bg-white p-8 rounded-2xl border border-[#EAF1EC] font-bold text-[#17211E] text-lg flex items-center gap-4 shadow-sm">
                <div className="w-2.5 h-2.5 rounded-full bg-[#174C3C]"></div>
                {type}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Approach */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[#17211E] mb-6">Project-Based Pricing for Agency Work</h2>
            <p className="text-xl text-[#17211E]/75 mb-10 font-medium leading-relaxed">
              Most design and development work is scoped as a project. Smaller technical tasks can use agency support rates or prepaid support blocks.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16">
            <div className="bg-[#FAF8F3] p-12 rounded-[2.5rem] border border-[#EAF1EC] shadow-sm">
              <h3 className="text-2xl font-extrabold text-[#17211E] mb-8 border-b border-[#EAF1EC] pb-6 uppercase tracking-wider">Technical Projects</h3>
              <div className="space-y-8">
                <div className="flex justify-between items-center text-xl font-bold text-[#17211E]">
                  <span>Website migrations</span>
                  <span className="text-[#174C3C]">starting at $199</span>
                </div>
                <div className="flex justify-between items-center text-xl font-bold text-[#17211E]">
                  <span>Performance optimization</span>
                  <span className="text-[#174C3C]">starting at $149</span>
                </div>
              </div>
              <p className="mt-12 text-lg text-[#17211E]/60 font-bold italic">
                Larger design and development projects are quoted after review. Fixed public package pricing is not listed on this page.
              </p>
            </div>

            <div className="bg-[#EAF1EC] p-12 rounded-[2.5rem] border border-[#3B7C6D]/10 shadow-sm">
              <h3 className="text-2xl font-extrabold text-[#17211E] mb-8 border-b border-[#3B7C6D]/10 pb-6 uppercase tracking-wider">Agency Support</h3>
              <div className="space-y-6 mb-10">
                <div className="flex justify-between items-center text-xl font-bold text-[#17211E]">
                  <span>Agency technical support</span>
                  <span className="text-[#174C3C]">Partner rate</span>
                </div>
                <div className="flex justify-between items-center text-xl font-bold text-[#17211E]">
                  <span>5-hour support block</span>
                  <span className="text-[#174C3C]">Prepaid</span>
                </div>
                <div className="flex justify-between items-center text-xl font-bold text-[#17211E]">
                  <span>10-hour support block</span>
                  <span className="text-[#174C3C]">Prepaid</span>
                </div>
              </div>
              <Link to="/services/technical-support" className="inline-flex items-center text-[#174C3C] font-extrabold hover:text-[#3B7C6D] transition-colors text-xl gap-3">
                Learn about Technical Support <SafeIcon icon={FiArrowRight} />
              </Link>
            </div>
          </div>
          
          <div className="text-center">
            <Link to="/digital-review" className="home-button">
              Discuss a Project
            </Link>
          </div>
        </div>
      </section>

      {/* After Launch */}
      <section className="py-24 bg-[#FAF8F3]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-20">
            <h2 className="text-4xl md:text-5xl font-bold text-[#17211E] mb-8 leading-tight">Launch Is Not the Finish Line</h2>
            <p className="text-xl text-[#17211E]/75 leading-relaxed font-medium">
              After a build or redesign, websites still need updates, security, backups, monitoring and support.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
            {[
              { title: "Website Management", text: "Ongoing maintenance, security, backups and monitoring.", link: "/services/website-management" },
              { title: "Managed Hosting", text: "Hosting managed by the same technical team.", link: "/services/managed-hosting" },
              { title: "Technical Support", text: "Help with issues, changes and improvements after launch.", link: "/services/technical-support" }
            ].map((item, i) => (
              <div key={i} className="bg-white p-12 rounded-[2.5rem] border border-[#EAF1EC] shadow-sm flex flex-col text-center">
                <h3 className="text-2xl font-extrabold text-[#17211E] mb-6">{item.title}</h3>
                <p className="text-lg text-[#17211E]/70 leading-relaxed font-medium mb-10 flex-grow">{item.text}</p>
                <Link to={item.link} className="text-[#174C3C] font-bold hover:text-[#3B7C6D] transition-colors text-lg flex items-center justify-center gap-2">
                  Learn More <SafeIcon icon={FiArrowRight} />
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Business Routing Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-[#FAF8F3] rounded-[2.5rem] p-12 md:p-20 shadow-xl border border-[#EAF1EC] text-center max-w-5xl mx-auto">
            <div className="text-sm font-bold tracking-[0.25em] text-[#D7B77A] uppercase mb-8">For Businesses</div>
            <h2 className="text-4xl md:text-5xl font-extrabold text-[#17211E] mb-8 leading-tight">Need Website Design for Your Own Business?</h2>
            <p className="text-xl text-[#17211E]/75 mb-12 font-medium leading-relaxed max-w-3xl mx-auto">
              Tell us what you need built or fixed, and we will help work out the next step.
            </p>
            <Link to="/digital-review" className="home-button">
              Tell Us What You Need Help With
            </Link>
          </div>
        </div>
      </section>

      {/* Related Services */}
      <section className="py-24 bg-[#FAF8F3]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-[#17211E] mb-16">Related Services</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6 mb-16">
            {[
              { label: "Website Management", link: "/services/website-management" },
              { label: "Technical Support", link: "/services/technical-support" },
              { label: "Managed Hosting", link: "/services/managed-hosting" },
              { label: "Business Email", link: "/services/business-email" },
              { label: "Agency Plans", link: "/agency-plans" },
              { label: "Technology Consulting", link: "/services/technology-consulting" }
            ].map((item, i) => (
              <Link key={i} to={item.link} className="bg-white p-8 rounded-2xl border border-[#EAF1EC] font-bold text-[#17211E] text-center hover:shadow-md transition-all">
                {item.label}
              </Link>
            ))}
          </div>
          <Link to="/services" className="home-button-secondary">
            Explore All Services
          </Link>
        </div>
      </section>

      {/* FAQ Accordion */}
      <section className="py-24 bg-white border-t border-[#EAF1EC]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-[#17211E] mb-16 text-center">Frequently Asked Questions</h2>
          <div className="space-y-4">
            {faqs.map((faq, i) => (
              <div key={i} className="bg-[#FAF8F3] border border-[#EAF1EC] rounded-2xl overflow-hidden shadow-sm">
                <button 
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  aria-expanded={openFaq === i}
                  aria-controls={`faq-answer-${i}`}
                  className="w-full flex justify-between items-center p-8 text-left hover:bg-white transition-colors"
                >
                  <span className="text-xl font-bold text-[#17211E]">{faq.q}</span>
                  <SafeIcon icon={FiChevronDown} className={`w-6 h-6 text-[#3B7C6D] transition-transform ${openFaq === i ? 'rotate-180' : ''}`} />
                </button>
                <div 
                  id={`faq-answer-${i}`} 
                  className={`px-8 pb-8 text-xl text-[#17211E]/75 font-medium leading-relaxed ${openFaq === i ? 'block' : 'hidden'}`}
                >
                  {faq.a}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-32 bg-[#174C3C] text-center relative overflow-hidden">
        <div className="absolute top-0 left-0 w-64 h-64 bg-white/5 rounded-full -translate-x-1/2 -translate-y-1/2"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-white/5 rounded-full translate-x-1/3 translate-y-1/3"></div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-10 tracking-tight leading-tight">
            Add Design and Development Capacity Without Growing Headcount
          </h2>
          <p className="text-2xl text-[#FAF8F3]/80 mb-14 max-w-3xl mx-auto leading-relaxed">
            Get dependable overflow support for builds, redesigns, migrations and technical website projects.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Link to="/digital-review" className="home-button-on-dark">
              Discuss a Project
            </Link>
            <Link to="/agency-plans" className="home-button-on-dark-secondary">
              View Agency Plans
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default WebDesignDevelopment;